<template>
  <div
    class="textarea"
    :class="{
      'textarea--focused': isFocus,
      'textarea--disabled': isDisabled,
    }"
    @click="clickFocusOutside"
  >
    <div
      class="textarea-container"
      :class="{
        'textarea-container--focused': isFocus,
        'textarea-container--with-value': value,
        'textarea-container--error': isWithError,
        'textarea-container--success': isWithSuccess,
        'textarea-container--disabled': isDisabled,
      }"
    >
      <div class="textarea-container__left-slot" v-if="isLeftTextareaAddons">
        <slot name="lefttextareaAddons"></slot>
      </div>
      <div class="textarea-container__textarea-wrapper">
        <span class="textarea-container__textarea-label">
          {{ label }}
        </span>
        <div style="margin-top: 1.75rem; margin-bottom: 0.125rem">
          <TirPmScrollbar
            :style="{ maxHeight: maxHeight, minHeight: minHeight }"
            show-on-hover
          >
            <div
              class="textarea-container__textarea"
              :contenteditable="!isDisabled && !isReadonly"
              ref="$contenteditable"
              :maxlength="maxLength"
              :placeholder="placeholder"
              tabindex="0"
              :autocomplete="computedIsAutocomplete"
              :spellcheck="spellcheck"
              @input="onInput"
              @focus="onFocus"
              @blur="onBlur"
              @beforeinput="onBeforeInput"
              @paste="onPaste"
              @drop="onDrop"
              @keydown="onKeydown"
            />
          </TirPmScrollbar>
        </div>
      </div>
      <div class="textarea-container__right-slot" v-if="isRightTextareaAddons">
        <slot name="righttextareaAddons"></slot>
      </div>
    </div>

    <div
      class="textarea__hint-message"
      v-if="isWithHint"
      :class="{
        'textarea__hint-message--error': isWithError,
        'textarea__hint-message--show': isShowHint,
        'textarea__hint-message--success': isWithSuccess,
      }"
    >
      <slot name="hintMessage"></slot>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  PropType,
  computed,
  onMounted,
  ref,
  useSlots,
  defineEmits,
  watch,
  defineExpose,
} from "vue";
import { TirPmScrollbar } from "tir-pm-scrollbar";

/** Слоты для кастомного контента */
const slots = useSlots();

/** Пропсы компонента */
const props = defineProps({
  /** Значение */
  modelValue: {
    type: String,
    default: "",
  },
  /** Лэйбл */
  label: {
    type: String as PropType<string>,
    default: "",
  },
  /** Заголовок */
  title: {
    type: String as PropType<string>,
    default: "",
  },
  /** Нужно ли автозаполнение */
  isAutocomplete: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  /** Плейсхолдер */
  placeholder: {
    type: String as PropType<string>,
    default: "",
  },
  /** Показывать ли подсказку всегда */
  isShowHintDefault: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  /** Показывать ли подсказку в данный момент */
  isShowHint: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  /** Показывать ли подсказку вообще, есть ли она, зарезервировать место для подсказки */
  isWithHint: {
    type: Boolean as PropType<boolean>,
    default: true,
  },
  /** Ошибка */
  isWithError: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  /** Успешное состояние */
  isWithSuccess: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  /** Дизейбл */
  isDisabled: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  /** Только для чтения */
  isReadonly: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  /** Максимальная длина */
  maxLength: {
    type: String as PropType<string>,
    default: undefined,
  },
  /** Автофокус */
  autoFocus: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  /** Автовыделение всего контента в инпуте */
  autoSelect: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  /** Максимальная высота */
  maxHeight: {
    type: String,
    default: "2.5rem",
  },
  /**Минимальная высота */
  minHeight: {
    type: String,
    default: "2.5rem",
  },
  /**Проверять текст на ошибки */
  spellcheck: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits<{
  /** Обновление значения модели */
  "update:modelValue": [value: string];
  /** Событие фокуса */
  focus: [event: FocusEvent];
  /** Событие потери фокуса */
  blur: [event: FocusEvent];
  /** Событие нажатия клавиши Enter */
  enter: [event: KeyboardEvent];
  /** Событие ввода */
  input: [event: InputEvent];
}>();

/** Стейт фокуса */
const isFocus = ref(false);
/** Ссылка на поле */
const $contenteditable = ref<HTMLTextAreaElement>();

/** Вычисляемое свойство для автозаполнения */
const computedIsAutocomplete = computed(() =>
  props.isAutocomplete ? "on" : "off"
);

/** Проверка наличия кастомного контента слева */
const isLeftTextareaAddons = computed(() => slots.leftTextareaAddons?.());
/** Проверка наличия кастомного контента справа */
const isRightTextareaAddons = computed(() => slots.rightTextareaAddons?.());

/** Связывание значения с пропом modelValue */
const value = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

/** Обработчик события ввода */
const onInput = (event: InputEvent) => {
  if (!$contenteditable.value) return;

  const textContent =
    $contenteditable.value.innerText === "\n"
      ? ""
      : $contenteditable.value.innerText;
  emit("input", event);
  value.value = textContent;
};

/** Обработчик события beforeinput для предотвращения форматирования */
const onBeforeInput = (event: InputEvent) => {
  if (event.inputType.startsWith("format")) {
    event.preventDefault();
  }
};

/** Обработчик вставки текста без форматирования */
const onPaste = (event: ClipboardEvent) => {
  event.preventDefault();
  // @ts-ignore
  const clipboardData = event.clipboardData || window.clipboardData;
  const text = clipboardData?.getData("text") || "";
  document.execCommand("insertText", false, text);
};

/**Получение узла и смещения для установки каретки по координатам мыши */
const getCaretPositionFromMouse = (
  element: HTMLElement,
  x: number,
  y: number
) => {
  let range;

  // Проверяем, какой метод доступен
  if (typeof document.caretRangeFromPoint === "function") {
    range = document.caretRangeFromPoint(x, y);
    // @ts-ignore
  } else if (typeof document.caretPositionFromPoint === "function") {
    // @ts-ignore
    const position = document.caretPositionFromPoint(x, y);
    if (position) {
      return {
        node: position.offsetNode,
        offset: position.offset,
      };
    }
  }

  // Если используется caretRangeFromPoint
  if (range) {
    return {
      node: range.startContainer,
      offset: range.startOffset,
    };
  }

  // Если не удалось найти позицию каретки
  return { node: element, offset: 0 };
};

/**Обработчик события drop */
const onDrop = (event: DragEvent) => {
  event.preventDefault();
  const data = event.dataTransfer?.getData("text/plain"); // Извлекаем только текст
  if (data && $contenteditable.value) {
    // Получаем координаты дропа
    const x = event.clientX;
    const y = event.clientY;
    const position = getCaretPositionFromMouse($contenteditable.value, x, y);
    const range = document.createRange();
    range.setStart(position.node, position.offset);
    range.collapse(true);
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
    document.execCommand("insertText", false, data);
  }
};

/** Фокусировка на поле */
const focus = () => {
  if (!$contenteditable.value) return;
  try {
    // Фокусируем элемент
    $contenteditable.value.focus();

    // Получаем Selection API
    const selection = window.getSelection();
    if (!selection) return;

    // Создаём новый диапазон
    const range = document.createRange();

    // Выбираем последний дочерний узел или сам элемент, если дочерних нет
    const lastChild =
      $contenteditable.value.lastChild || $contenteditable.value;

    // Устанавливаем позицию в конец содержимого
    const lastOffset = lastChild.textContent?.length || 0;
    range.setStart(lastChild, lastOffset);
    range.setEnd(lastChild, lastOffset);

    // Применяем диапазон
    selection.removeAllRanges();
    selection.addRange(range);
  } catch (e) {
    console.error(e);
  }
};

/** Выделение всего текста */
const select = () => $contenteditable.value?.select();

/** Проверка автофокуса и автовыделения */
const checkAutofocusOrAutoselect = () => {
  if (!$contenteditable.value) return;

  if (props.autoSelect) {
    select();
    return;
  }

  if (props.autoFocus) {
    focus();
    return;
  }
};

/** Обработчик клика вне элемента для фокуса */
const clickFocusOutside = () => {
  if (props.isDisabled && isFocus.value) return;
  if ($contenteditable.value) $contenteditable.value.focus();
};

/** Обработчик фокуса */
const onFocus = (e: FocusEvent) => {
  if (props.isDisabled) return;
  isFocus.value = true;
  emit("focus", e);
};

/** Обработчик потери фокуса */
const onBlur = (e: FocusEvent) => {
  if (props.isDisabled) return;
  isFocus.value = false;
  emit("blur", e);
};

/**Обработчик события keydown */
const onKeydown = (e: KeyboardEvent) => {
  if (e.key === "Enter") {
    emit("enter", e);
  }
};

/** Наблюдатель за изменением modelValue */
watch(
  () => props.modelValue,
  (newValue) => {
    if ($contenteditable.value) {
      const textContent =
        $contenteditable.value.innerText === "\n"
          ? ""
          : $contenteditable.value.innerText;
      const isChanged = textContent !== newValue;
      if (isChanged) $contenteditable.value.innerText = newValue;
    }
  }
);

/** Инициализация значений после монтирования компонента */
onMounted(() => {
  if ($contenteditable.value) {
    $contenteditable.value.innerText = props.modelValue;
  }
  checkAutofocusOrAutoselect();
});

/** Экспозиция публичных методов для внешнего использования */
defineExpose({
  focus,
  select,
});
</script>

<style lang="scss" scoped>
@use "sass:map";

@import "tir-style-system/expose-variables";
@import "tir-style-system/expose-default-theme";
@import "tir-style-system/core/mixins";

.textarea {
  display: flex;
  flex-direction: column;
  position: relative;

  &__hint-message {
    @include set-line-text-size(0.75);
    color: var(--text-secondary, map.get($colors, "text-secondary"));
    visibility: hidden;
    margin-top: 0.25rem;
    min-height: 1rem;
    margin-bottom: 0.25rem;
  }

  &__hint-message--show {
    visibility: visible;
  }

  &__hint-message--error {
    color: var(--text-negative, map.get($colors, "text-negative"));
  }

  &__hint-message--success {
    color: var(--text-info, map.get($colors, "text-info"));
  }
}

.textarea-container {
  $self: &;
  position: relative;
  background-color: var(--neutral-10, map.get($colors, "neutral-10"));
  border-radius: var(--8, map.get($margins, "8"));
  border: 0.125rem solid var(--neutral-10, map.get($colors, "neutral-10"));
  cursor: text;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  transition: border 0.25s ease-in-out, background-color 0.25s ease-in-out;

  &:hover {
    border: 0.125rem solid var(--neutral-90, map.get($colors, "neutral-90"));
  }

  &--focused {
    border: 0.125rem solid var(--neutral-50, map.get($colors, "neutral-50"));
    background-color: var(--neutral-0, map.get($colors, "neutral-0"));
    box-sizing: border-box;
  }

  &--focused:hover {
    border: 0.125rem solid var(--neutral-90, map.get($colors, "neutral-90"));
  }

  &__left-slot {
    display: flex;
  }

  &__textarea-wrapper {
    position: relative;
    display: flex;
    flex-grow: 1;
    flex-direction: column;
    justify-content: end;
    margin-left: 0.625rem;
  }

  &__textarea-label {
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: var(--text-secondary, map.get($colors, "text-secondary"));
    transition: transform 0.2s ease-in-out, top 0.2s ease-in-out;
    position: absolute;
    top: 0.375rem;
    transform: translateY(0);
    user-select: none;
  }

  &__textarea {
    outline: none;
    background-color: transparent;
    border: none;
    font-size: 1rem;
    line-height: 1.25rem;
    resize: none;
    width: 100%;
    max-width: 100%;
    padding: 0;
    padding-right: 0.75rem;
    color: var(--text-primary, map.get($colors, "text-primary"));
    opacity: 0;
    word-break: break-all;
    height: min-content;
    appearance: textfield;
    display: block;
    font-family: inherit;
    overflow: hidden;

    &::placeholder {
      color: var(--text-tertiary, map.get($colors, "text-tertiary"));
    }

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    &:-webkit-autofill {
      -webkit-box-shadow: inset 0 0 0 3.125rem
        var(--neutral-10, map.get($colors, "neutral-10")) !important;
      box-shadow: inset 0 0 0 3.125rem
        var(--neutral-10, map.get($colors, "neutral-10")) !important;
    }

    &:-webkit-autofill:focus {
      -webkit-box-shadow: inset 0 0 0 3.125rem
        var(--neutral-0, map.get($colors, "neutral-0")) !important;
      box-shadow: inset 0 0 0 3.125rem
        var(--neutral-0, map.get($colors, "neutral-0")) !important;
    }
  }

  &--focused &__textarea-label,
  &--with-value &__textarea-label {
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: var(--text-secondary, map.get($colors, "text-secondary"));
    top: 0.375rem;
    transform: translateY(0);
  }

  &--focused &__textarea,
  &--with-value &__textarea {
    opacity: 1;
  }

  &--error {
    border: 0.125rem solid
      var(--status-negative, map.get($colors, "status-negative"));
  }

  &--success {
    border: 0.125rem solid var(--status-info, map.get($colors, "status-info"));
  }

  &--disabled {
    background-color: var(--neutral-05, map.get($colors, "neutral-05"));
    border-color: var(--neutral-05, map.get($colors, "neutral-05"));
    pointer-events: none;
  }

  &--disabled &__textarea-label {
    color: var(--text-tertiary, map.get($colors, "text-tertiary"));
  }

  &--disabled &__textarea {
    color: var(--text-tertiary, map.get($colors, "text-tertiary"));
  }

  &__left-slot,
  &__right-slot {
    display: flex;
    gap: 0.25rem;
  }

  &__left-slot {
    padding-left: 0.625rem;
    padding-right: 0.125rem;
  }

  &__right-slot {
    padding-right: 0.625rem;
    padding-left: 0.125rem;
  }
}
</style>
